import { Delta } from 'quill/core';
import React from 'react';
import { Link } from '../utils/router/v3Bridge';
import DeltaPreview from '../components/DeltaPreview';
import InfiniteScrollWaypoint from '../components/InfiniteScrollWaypoint';
import renderHighlight from '../utils/search/renderHighlight';
import { publicPostPath } from '../utils/url/postPath';
import styles from './PublicSearchResults.module.scss';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';

const PUBLIC_SEARCH_RESULTS_POST_FRAGMENT = gql(`
  fragment PublicSearchResults_publicPostSearchResult on PublicPostSearchResult {
    content
    highlight
    title
    post {
      id
      title
      topics {
        id
        name
        ancestors {
          id
          name
        }
      }
      ...publicPostPath_post
    }
  }
`);

const StringReplace = ({
  text,
  value,
  render,
}: {
  text: string;
  value: string;
  render(value: string): React.ReactNode;
}) => {
  const partsWithoutValue = text.split(new RegExp(value, 'i'));

  const { parts } = partsWithoutValue.reduce<{
    parts: (JSX.Element | string)[];
    index: number;
  }>(
    (acc, part, i) => {
      const start = acc.index + part.length;
      const end = start + value.length;
      if (i < partsWithoutValue.length - 1) {
        return {
          parts: [
            ...acc.parts,
            // eslint-disable-next-line react/no-array-index-key
            <React.Fragment key={i}>
              {part}
              {render(text.substring(start, end))}
            </React.Fragment>,
          ],
          index: end,
        };
      }

      return { parts: [...acc.parts, part], index: end };
    },
    { parts: [], index: 0 },
  );

  return <>{parts}</>;
};

interface PublicSearchResultsProps {
  query: string;
  results: DocumentType<typeof PUBLIC_SEARCH_RESULTS_POST_FRAGMENT>[];
  onFetchMore: null | (() => void);
}

const PublicSearchResults = ({
  query,
  results,
  onFetchMore,
}: PublicSearchResultsProps) => {
  return (
    <div>
      {results.map((result) => {
        const highlightDelta = new Delta(result.highlight);
        const content = new Delta(result.content).compose(highlightDelta);

        const firstTopic = result.post.topics[0];

        const topics = firstTopic
          ? [...firstTopic.ancestors.slice().reverse(), firstTopic]
          : [];

        return (
          <Link
            to={publicPostPath(result.post)}
            className={styles.resultRow}
            key={result.post.id}
          >
            <h2 className={styles.pageTitle}>
              {result.title ? renderHighlight(result.title) : null}
            </h2>
            <DeltaPreview
              typography="publicSearchResult"
              maxLines={5}
              link={false}
              content={content}
            />
            {firstTopic && (
              <div className={styles.topics}>
                {topics.map((topic, i) => {
                  return (
                    <React.Fragment key={topic.id}>
                      <StringReplace
                        text={topic.name}
                        value={query}
                        render={(value) => (
                          <em className="search-highlight">{value}</em>
                        )}
                      />
                      {i < topics.length - 1 && ' > '}
                    </React.Fragment>
                  );
                })}
              </div>
            )}
          </Link>
        );
      })}
      <InfiniteScrollWaypoint fetchMore={onFetchMore} />
    </div>
  );
};

export default PublicSearchResults;
